import { CHALLENGE_TOGGLE, DESIGN_2_0, PROGRAMS } from 'constants/featureFlags'
import { fetchAndActivate, getString } from 'firebase/remote-config'
import { useEffect, useMemo, useState } from 'react'

import { remoteConfig } from 'utils/firebase/initialize.web'

import { FeatureFlag, RemoteFeatureFlag } from 'types/featureFlag'

import { useCurrentUser } from './useCurrentUser'

const computeFeatureFlagValue = (
  flag: RemoteFeatureFlag,
  currentUsername?: string,
): FeatureFlag => {
  let enabled: boolean = flag.isEnabled
  if (currentUsername && flag.enabledUsernames && flag.enabledUsernames.includes(currentUsername)) {
    enabled = true
  }

  if (
    currentUsername &&
    flag.disabledUsernames &&
    flag.disabledUsernames.includes(currentUsername)
  ) {
    enabled = false
  }

  return { key: flag.name, value: enabled }
}

export const useFeatureFlags = () => {
  const { data: currentUser } = useCurrentUser()
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([])

  const fetchRemoteConfigs = async () => {
    const flags: FeatureFlag[] = []

    await fetchAndActivate(remoteConfig)

    const challengeToggleValue = getString(remoteConfig, CHALLENGE_TOGGLE)
    if (challengeToggleValue && challengeToggleValue.length > 0) {
      const challengeToggle = JSON.parse(challengeToggleValue) as RemoteFeatureFlag
      const challengeFlag = computeFeatureFlagValue(challengeToggle)
      flags.push(challengeFlag)
    }

    const programsValue = getString(remoteConfig, PROGRAMS)
    if (programsValue && programsValue.length > 0) {
      const programs = JSON.parse(programsValue) as RemoteFeatureFlag
      const programsFlag = computeFeatureFlagValue(programs)
      flags.push(programsFlag)
    }

    const newDesignValue = getString(remoteConfig, DESIGN_2_0)
    if (newDesignValue && newDesignValue.length > 0) {
      const newDesign = JSON.parse(newDesignValue) as RemoteFeatureFlag
      const newDesignFlag = computeFeatureFlagValue(newDesign, currentUser?.username)
      flags.push(newDesignFlag)
    }

    setFeatureFlags(flags)
  }

  useEffect(() => {
    fetchRemoteConfigs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getFlagStatus = (flagName: string) => {
    if (!featureFlags.length) return false
    const flagIndex = featureFlags.findIndex((flag) => flag.key === flagName)
    return flagIndex > -1 ? featureFlags[flagIndex].value : false
  }

  const isChallengeToggleEnabled: boolean = useMemo(() => {
    return getFlagStatus(CHALLENGE_TOGGLE)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags])

  const isProgramsFlagEnabled: boolean = useMemo(() => {
    return getFlagStatus(PROGRAMS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags])

  const isNewDesignFlagEnabled: boolean = useMemo(() => {
    return getFlagStatus(DESIGN_2_0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags])

  return {
    isProgramsFlagEnabled,
    isChallengeToggleEnabled,
    isNewDesignFlagEnabled,
  }
}
